/**
 * "This week" summary card — Mon-Sun rollup of rides, hours, km, TSS.
 *
 * Visible at the top of the Rides page in both list and calendar
 * modes. Lives in its own file because the grouping is reused (the
 * mobile calendar uses the same date logic to compute per-week
 * strips).
 */
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { LucideIcon } from '../components/icons/LucideIcon.tsx';
import { useRidesInRange } from '../hooks/useRides.ts';
import { percentChange, totalsFor, type WeekTotals } from '../lib/ridesHome.ts';

export function WeeklySummary() {
  const { t } = useTranslation();
  const { monday, sunday } = useMemo(() => weekBoundsFor(new Date()), []);

  const { data, isLoading } = useRidesInRange(
    toIsoDate(monday),
    toIsoDate(sunday),
  );
  const totals = useMemo(() => totalsFor(data?.rides ?? []), [data]);
  const lastWeek = useLastWeekSoFar(monday);

  let body: ReactNode;
  if (isLoading) {
    body = <div className="h-10 rounded-lg bg-(--color-bg-tertiary) animate-pulse" />;
  } else if (totals.rides === 0) {
    body = (
      <p className="text-sm text-(--color-text-secondary)">
        {t('dashboard.weekSummary.empty')}
      </p>
    );
  } else {
    body = (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <Stat
          label={t('dashboard.weekSummary.rides')}
          value={`${totals.rides}`}
        />
        <Stat
          label={t('dashboard.weekSummary.hours')}
          value={`${totals.hours.toFixed(1)}h`}
        />
        <Stat
          label={t('dashboard.weekSummary.distance')}
          value={`${totals.km.toFixed(0)} km`}
        />
        <Stat
          label={t('dashboard.weekSummary.tss')}
          value={`${Math.round(totals.tss)}`}
        />
      </div>
    );
  }
  if (!isLoading && lastWeek) {
    body = (
      <>
        {body}
        <WeekChangeLine current={totals} previous={lastWeek} />
      </>
    );
  }

  return (
    <section className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
          {t('dashboard.weekSummary.title')}
        </h2>
        <span className="text-xs text-(--color-text-secondary)">
          {formatRange(monday, sunday)}
        </span>
      </div>

      {body}
    </section>
  );
}

function Stat({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) p-2 text-center">
      <div className="text-[10px] text-(--color-text-secondary) uppercase tracking-wide">
        {label}
      </div>
      <div className="text-(--color-text-primary) font-semibold">{value}</div>
    </div>
  );
}

/** Last week from Monday to the same weekday as today (rides only). */
function useLastWeekSoFar(monday: Date): WeekTotals | null {
  const { from, to } = useMemo(() => {
    const start = new Date(monday);
    start.setDate(start.getDate() - 7);
    const end = new Date();
    end.setDate(end.getDate() - 7);
    return { from: toIsoDate(start), to: toIsoDate(end) };
  }, [monday]);
  const { data } = useRidesInRange(from, to);
  return useMemo(() => (data ? totalsFor(data.rides) : null), [data]);
}

/** "TSS up 12% · Time down 5% vs this point last week"; muted, no colour
 * for better or worse (docs/RIDES_UX_PLAN.md §3.3). */
export function WeekChangeLine({ current, previous }: Readonly<{ current: WeekTotals; previous: WeekTotals }>) {
  const { t } = useTranslation();
  const items = [
    { metric: t('dashboard.weekSummary.tss'), percent: percentChange(current.tss, previous.tss) },
    { metric: t('dashboard.weekSummary.hours'), percent: percentChange(current.hours, previous.hours) },
  ].filter((item): item is { metric: string; percent: number } => item.percent !== null);
  if (items.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-(--color-text-secondary)">
      {items.map(({ metric, percent }) => (
        <span key={metric} className="inline-flex items-center gap-1">
          <LucideIcon name={trendIcon(percent)} size={14} />
          {changeText(t, metric, percent)}
        </span>
      ))}
      <span>{t('dashboard.weekSummary.vsLastWeek')}</span>
    </p>
  );
}

function trendIcon(percent: number): 'trending-up' | 'trending-down' | 'minus' {
  if (percent > 0) return 'trending-up';
  return percent < 0 ? 'trending-down' : 'minus';
}

function changeText(t: TFunction, metric: string, percent: number): string {
  if (percent > 0) return t('dashboard.weekSummary.up', { metric, percent });
  if (percent < 0) return t('dashboard.weekSummary.down', { metric, percent: -percent });
  return t('dashboard.weekSummary.same', { metric });
}

/** Mon-Sun bounds for the ISO week containing ``ref``. */
function weekBoundsFor(ref: Date): { monday: Date; sunday: Date } {
  const monday = new Date(ref);
  // Mon-first: getDay() is 0..6 (Sun..Sat); shift so Mon=0.
  const offset = (monday.getDay() + 6) % 7;
  monday.setDate(monday.getDate() - offset);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  return { monday, sunday };
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatRange(start: Date, end: Date): string {
  const sFmt = start.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const eFmt = end.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return `${sFmt} → ${eFmt}`;
}
